import { useQueryClient, useMutation } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import { TakerMarketGroupType } from '@/enterprise/data/useGroupByPricingType';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { isOfferValidForUpdate } from '@/enterprise/features/globalOffers/utils';
import apiClient from '@/lib/apiClient';
import { usePendingOfferPollingStore } from '@/lib/offerStatusPolling/usePendingOfferPollingStore';
import { useRefetchStatsQueries } from '@/lib/serverSentEvents/useServerSideEventListeners';
import { useReporting } from '@/reporting';

type OfferType = 'price-discovery' | 'benchmark' | 'static';

type PriceDiscoveryRequestData =
  paths['/offers/price-discovery']['patch']['requestBody']['content']['application/json'];
type BenchmarkRequestData = paths['/offers/benchmark']['patch']['requestBody']['content']['application/json'];
type StaticRequestData = paths['/offers/static']['patch']['requestBody']['content']['application/json'];

interface GlobalOfferArgs {
  isEnabled?: boolean;
  isDiscountBidding?: boolean; // price discovery specific
  maxApr?: number; // price discovery specific
  maxDiscount?: number; // price discovery specific
}

export type GlobalOfferMarketPricingType = Extract<TakerMarketGroupType, 'BENCHMARK' | 'FIXED' | 'NAME_YOUR_RATE'>;

interface GlobalOfferConfig {
  offerType: OfferType;
  takerMarkets: TakerMarket[];
}

interface GlobalOfferResult {
  marketUuid: string;
  supplierDivisionUuid: string;
  frequencyChanged?: boolean;
  responseStatus: number;
}

const useGlobalOffer = ({ offerType, takerMarkets }: GlobalOfferConfig) => {
  const queryClient = useQueryClient();
  const { refetchManyTakerMarkets } = useRefetchStatsQueries();
  const { trackEnterpriseEvent } = useReporting();
  const { addPendingMarkets } = usePendingOfferPollingStore();

  return useMutation({
    mutationKey: [`set${offerType}GlobalOffer`],
    mutationFn: async (args: GlobalOfferArgs) => {
      const { isEnabled, isDiscountBidding, maxApr, maxDiscount } = args;

      // filter valid offers for update
      const validTakerMarkets = takerMarkets?.filter((tm) => isOfferValidForUpdate(tm, isEnabled)) || [];

      if (validTakerMarkets.length === 0) {
        return [];
      }

      // separate one-time offers
      const shouldConvertOneTimeOffer = (tm: TakerMarket) =>
        tm.offerConfig.frequency === 'ONE_TIME' &&
        isEnabled === false && // toggling off
        offerType === 'price-discovery';

      const oneTimeOffersToRecurring = validTakerMarkets.filter(shouldConvertOneTimeOffer);
      const regularOffers = validTakerMarkets.filter((tm) => !shouldConvertOneTimeOffer(tm));

      const results: GlobalOfferResult[] = [];

      // handle one-time offers that need to be converted to paused recurring
      if (oneTimeOffersToRecurring.length > 0) {
        const oneTimeOfferData = {
          groupingKey: 'SUPPLIER_MARKET',
          frequency: 'recurring',
          status: 'paused',
          offers: oneTimeOffersToRecurring.map((tm) => ({
            makerOrganizationUuid: tm.makerOrganizationUuid,
            uuid: tm.offerConfig.uuid!,
          })),
        };

        const oneTimeResponse = await apiClient.patch(`api/supplier-experience/offers/price-discovery`, {
          json: oneTimeOfferData,
        });

        results.push(
          ...oneTimeOffersToRecurring.map((tm) => ({
            marketUuid: tm.marketUuid,
            supplierDivisionUuid: tm.takerDivisionUuid,
            frequencyChanged: true, // flag to indicate frequency changed from ONE_TIME to RECURRING
            responseStatus: oneTimeResponse.status,
          }))
        );
      }

      // handle regular offers with standard logic
      if (regularOffers.length > 0) {
        const offers = regularOffers.map((tm) => ({
          uuid: tm.offerConfig.uuid!,
          makerOrganizationUuid: tm.makerOrganizationUuid,
        }));

        const status = isEnabled !== undefined ? (isEnabled ? 'active' : 'paused') : undefined;

        const requestData = {
          offers,
          status,
        } satisfies PriceDiscoveryRequestData | BenchmarkRequestData | StaticRequestData;

        // add rate information for price discovery
        if (offerType === 'price-discovery') {
          if (isDiscountBidding && maxDiscount !== undefined) {
            (requestData as PriceDiscoveryRequestData).rateInformation = {
              rate: maxDiscount,
              rateType: 'DISC',
            };
          } else if (!isDiscountBidding && maxApr !== undefined) {
            (requestData as PriceDiscoveryRequestData).rateInformation = {
              rate: maxApr,
              rateType: 'APR',
            };
          }
        }

        const regularResponse = await apiClient.patch(`api/supplier-experience/offers/${offerType}`, {
          json: requestData,
        });

        results.push(
          ...regularOffers.map((tm) => ({
            marketUuid: tm.marketUuid,
            supplierDivisionUuid: tm.takerDivisionUuid,
            frequencyChanged: false,
            responseStatus: regularResponse.status,
          }))
        );
      }

      return results;
    },
    onSuccess: async (data, { isEnabled, isDiscountBidding, maxApr, maxDiscount }) => {
      const updatedTakerMarkets = data ?? [];

      // update cache
      queryClient.setQueryData<TakerMarket[]>(['taker-markets'], (prevTakerMarkets) =>
        (prevTakerMarkets ?? []).map((tm) => {
          const updatedData = updatedTakerMarkets.find(
            (tmData) => tmData.marketUuid === tm.marketUuid && tmData.supplierDivisionUuid === tm.takerDivisionUuid
          );

          if (updatedData) {
            const offerConfigUpdate = {
              ...(typeof isEnabled !== 'undefined' ? { isEnabled } : {}),
              ...(offerType === 'price-discovery' && typeof isDiscountBidding !== 'undefined'
                ? {
                    isDiscountBidding,
                    ...(isDiscountBidding ? { maxDiscount } : { maxApr }),
                  }
                : {}),
              ...(updatedData.frequencyChanged ? { frequency: 'RECURRING' as const } : {}),
            };

            return {
              ...tm,
              offerConfig: {
                ...tm.offerConfig,
                ...offerConfigUpdate,
              },
              ...(updatedData.frequencyChanged ? { disableAfterMarketClearsDate: null } : {}),
            };
          }

          return tm;
        })
      );

      const pendingMarkets: { marketUuid: string; takerDivisionUuid: string; makerOrganizationUuid: string }[] = [];
      const marketsToRefetch: { marketUuid: string; takerUuid: string }[] = [];

      updatedTakerMarkets.forEach((tm) => {
        const tmMeta = takerMarkets?.find(
          (meta) => meta.marketUuid === tm.marketUuid && meta.takerDivisionUuid === tm.supplierDivisionUuid
        );

        if (!tmMeta?.marketUuid || !tmMeta?.takerDivisionId) {
          return;
        }

        if (tm.responseStatus === 202) {
          pendingMarkets.push({
            marketUuid: tmMeta.marketUuid,
            takerDivisionUuid: tmMeta.takerDivisionUuid,
            makerOrganizationUuid: tmMeta.makerOrganizationUuid,
          });
        } else {
          // offer resolved synchronously, refetch immediately
          trackEnterpriseEvent('polling-update::in-time', {
            marketUuid: tmMeta.marketUuid,
            takerDivisionUuid: tmMeta.takerDivisionUuid,
            makerOrganizationUuid: tmMeta.makerOrganizationUuid,
          });
          marketsToRefetch.push({ marketUuid: tmMeta.marketUuid, takerUuid: tmMeta.takerDivisionUuid });
        }
      });

      if (pendingMarkets.length > 0) {
        addPendingMarkets(pendingMarkets);
      }

      if (marketsToRefetch.length > 0) {
        refetchManyTakerMarkets(marketsToRefetch);
      }
    },
  });
};

export const useGlobalPriceDiscoveryOffer = (takerMarkets: TakerMarket[]) =>
  useGlobalOffer({
    offerType: 'price-discovery',
    takerMarkets,
  });

export const useGlobalVariableRateOffer = (takerMarkets: TakerMarket[]) =>
  useGlobalOffer({
    offerType: 'benchmark',
    takerMarkets,
  });

export const useGlobalFixedRateOffer = (takerMarkets: TakerMarket[]) =>
  useGlobalOffer({
    offerType: 'static',
    takerMarkets,
  });
